// WithholdingTaxModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Stack, Button, Modal, RadioGroup, FormControlLabel, Radio, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import CustomDeleteConfirmationModal from '../../CustomDeleteConfirmationModal/CustomDeleteConfirmationModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import TaxAuthorityService from '../../../Services/taxAuthority.service';
import ClientService from '../../../Services/client.service';
import ReportsService from '../../../Services/reports.service';
import PartnerService from '../../../Services/partner.service';
import CaseRelationshipService from '../../../Services/caseRelationship.service';
import Constants from '../../../assests/Constants/constants';
import TaxAdvancesReportImage from '../../../assests/images/TaxAdvancesReport.jpg';
import PuffLoader from "react-spinners/PuffLoader";
import { beautifyNumber, roundVatToInteger } from '../../../Utils/FormatNumber';
import CustomMonthYearIndividualDatePicker from '../CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import BiMonthlyPeriodModal from '../../ClientDetails/Modals/BiMonthlyPeriodModal';
import { WithholdingModalHeader, AccumulatedAdvancePanel, WithholdingModalActions } from './WithholdingTaxModalComponents';
import './WithholdingTaxModal.css';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '92%',
    maxWidth: '1400px',
    height: '92vh',
    borderRadius: '16px',
    background: '#FFF',
    boxShadow: '0 12px 40px rgba(48, 79, 255, 0.12), 0 4px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

const formatDateToYearMonth = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${year}/${month}`;
};
const formatMonthYearDate = dateString => {
    const date = new Date(dateString);

    const month = ('0' + (date.getUTCMonth() + 1)).slice(-2);
    const year = date.getUTCFullYear();
    return `${month}/${year}`;
};
export default function WithholdingTaxModal({
    open,
    onClose,
    item,
    user,
    token,
    startDate,
    endDate,
    isRange,
    isBiMonthly,
    date,
    biStartDate,
    biEndDate,
    showBiMonth,
    setShowBiMonth,
    handleStartDateChangeNew,
    handleEndDateChangeNew,
    handleDateChangeNew,
    handleBiMonthDate,
    handleRangeChange,
    isMainLoading,
    onDataUpdate = () => { }, // Callback: (updatedData, partnerId, dateContext) => void
    selectedPartner = null,
    // Advanced
    advancedDate,
    setAdvancedDate,
    advancedStartDate,
    setAdvancedStartDate,
    advancedEndDate,
    setAdvancedEndDate,
    advancedBiStartDate,
    setAdvancedBiStartDate,
    advancedBiEndDate,
    setAdvancedBiEndDate,
    isAdvancedBiMonthly,
    // Shared case props
    isSubordinateClient = false,
    mainClientData = null,
    // Per-partner per-year resolved advance settings (keyed by partner Guid string).
    // Comes from GetVatAdvancesPageData; falls back to legacy partner fields when entry is missing.
    partnerAdvanceSettings = {}
}) {
    
    const { t, i18n } = useTranslation();
    const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';

    // Main local state for WithholdingTax
    const [withholdingTax, setWithholdingTax] = useState({});
    const [dateRange, setDateRange] = useState('');
    // ReportingWay => "automatic", "manual", or "unreported"
    const [reportingWay, setReportingWay] = useState('unreported');

    // Confirmation modal logic
    const [actionType, setActionType] = useState('');
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

    // Loading state for async operations
    const [loading, setLoading] = useState(false);

    // Data we might pass to the Tax Authority flow
    const [taxAuthorityData, setTaxAuthorityData] = useState({
        authUrl: '',
        resumeTaxAuthorityAdvancesOperation: false,
        UpdatedAdd: {},
    });

    // For rendering HTML that becomes PDF
    const withholdingContentRef = useRef(null);
    const [withholdingHtmlContent, setWithholdingHtmlContent] = useState('');

    const initializedItemKeyRef = useRef(null);
    const accumulatedAdvanceRequestIdRef = useRef(0);
    const fetchPartnersInProgressRef = useRef(false);

    const [accumulatedAdvance, setAccumulatedAdvance] = useState(null);
    
    // Partner-related state
    const [partners, setPartners] = useState([]);
    const [internalSelectedPartner, setInternalSelectedPartner] = useState('');
    const [originalWithholdingTax, setOriginalWithholdingTax] = useState({});
    
    // Log partner name when internalSelectedPartner changes
    useEffect(() => {
        if (internalSelectedPartner && partners.length > 0) {
            const partner = partners.find(p => p.id === internalSelectedPartner);
            if (partner) {
                console.log("Selected Partner:", partner.name, "ID:", internalSelectedPartner);
            } else {
                console.log("Partner not found for ID:", internalSelectedPartner);
            }
        }
    }, [internalSelectedPartner, partners]);
    
    // Helper function to get all related case IDs for shared reporting
    const getAllRelatedCaseIds = async (caseId, token) => {
        try {
            // Check if this case has relationships
            const relationships = await CaseRelationshipService.GetCaseRelationshipsByCaseId(token, caseId);
            
            if (!relationships || relationships.length === 0) {
                // No relationships, return just the current case
                return { caseIds: [caseId], mainCaseId: caseId, isSubordinate: false };
            }

            // Find the main case ID
            let mainCaseId = caseId;
            const relationship = relationships.find(rel => rel.SubordinateCaseId === caseId);
            const isSubordinate = !!relationship;
            if (relationship) {
                mainCaseId = relationship.MainCaseId;
            }

            // Get all subordinate case IDs
            const subordinateCaseIds = await CaseRelationshipService.GetSubordinateCaseIds(token, mainCaseId);
            
            // Return main case + all subordinate cases
            return { 
                caseIds: [mainCaseId, ...subordinateCaseIds], 
                mainCaseId: mainCaseId, 
                isSubordinate: isSubordinate 
            };
        } catch (error) {
            console.error('Error getting related case IDs:', error);
            return { caseIds: [caseId], mainCaseId: caseId, isSubordinate: false }; // Fallback to current case only
        }
    };


    useEffect(() => {
        if (!item?.clientCaseId || !token) return;

        accumulatedAdvanceRequestIdRef.current += 1;
        const thisRunId = accumulatedAdvanceRequestIdRef.current;

        const fetchAccumulatedAdvance = async () => {
            try {
                const dateToFetch = isBiMonthly ? (biEndDate ?? new Date()) : (date ?? new Date());
                const { caseIds: relatedCaseIds } = await getAllRelatedCaseIds(item.clientCaseId, token);

                const allDataResults = await Promise.all(
                    relatedCaseIds.map(caseId =>
                        ReportsService.getAccumulatedDifferenceInAdvance(caseId, user.accountantId, dateToFetch, token, selectedPartner?.id || null)
                    )
                );

                if (thisRunId !== accumulatedAdvanceRequestIdRef.current) return;

                const combinedData = allDataResults.reduce((sum, data) => {
                    if (!data) return sum;
                    return {
                        incomesNoVats: (sum.incomesNoVats || 0) + (data.incomesNoVats || data.IncomesNoVats || 0),
                        acceptedWithholdingTax: (sum.acceptedWithholdingTax || 0) + (data.acceptedWithholdingTax || data.AcceptedWithholdingTax || 0),
                        incomeTaxAdvances: (sum.incomeTaxAdvances || 0) + (data.incomeTaxAdvances || data.IncomeTaxAdvances || 0),
                        total: (sum.total || 0) + (data.total || data.Total || 0)
                    };
                }, {});

                setAccumulatedAdvance(combinedData);
            } catch (error) {
                if (thisRunId !== accumulatedAdvanceRequestIdRef.current) return;
                console.error("Error fetching accumulated advance:", error);
                setAccumulatedAdvance(null);
            }
        };

        const fetchPartners = async () => {
            if (fetchPartnersInProgressRef.current) return;
            fetchPartnersInProgressRef.current = true;
            try {
                const response = await PartnerService.getPartnersByCaseId(item.clientCaseId, token);
                if (response.ok) {
                    const partnersData = await response.json();
                    setPartners(partnersData);
                }
            } catch (error) {
                console.error("Error fetching partners:", error);
                setPartners([]);
            } finally {
                fetchPartnersInProgressRef.current = false;
            }
        };

        fetchAccumulatedAdvance();
        fetchPartners();
    }, [item?.clientCaseId, item?.StartDate, item?.EndDate, token]);

    // Initialize with passed selectedPartner when modal opens
    useEffect(() => {
        if (open && selectedPartner) {
            setInternalSelectedPartner(selectedPartner.id);
            // Don't call handlePartnerChange here - let the main useEffect handle it
        } else if (open && !selectedPartner && partners.length > 0) {
            // If no partner is passed but partners exist, select the first partner by default
            setInternalSelectedPartner(partners[0].id);
        }
    }, [open, selectedPartner, partners]);

    // ----------------------------------
    // 1) Build / update data from `item`
    // ----------------------------------
    useEffect(() => {
        if (!open) { initializedItemKeyRef.current = null; return; }
        if (!item) return;

        const itemKey = `${item.id ?? ''}_${item.clientCaseId ?? ''}_${item.StartDate}_${item.EndDate}_${item.total}_${item.witholdingIncomesNoVat}`;
        const hasItemChanged = initializedItemKeyRef.current !== itemKey;

        if (hasItemChanged) {
            initializedItemKeyRef.current = itemKey;

            const {
                id, clientName, clientLocation, clientCaseId, taxClientNumber,
                AcceptedWithholdingTax, AcceptedWithholdingTaxMaximum,
                incomeTaxAdvancePercentage, IncomeTaxAdvances, total,
                witholdingIncomesNoVat, StartDate, EndDate,
                startDate: startDateLower, endDate: endDateLower,
                useIncomeTaxAdvancePercentage, incomeTaxAdvanceAmount,
                surplusExpenses
            } = item;

            const rawTaxClientNumber =
                taxClientNumber ?? item.taxClientNumber ?? item.TaxClientNumber;
            const trimmedTaxClientNumber =
                rawTaxClientNumber != null && String(rawTaxClientNumber).trim() !== ''
                    ? String(rawTaxClientNumber).trim()
                    : '';

            const start = StartDate || startDateLower || new Date();
            const end = EndDate || endDateLower || new Date();

            const builtTax = {
                id: id || null,
                clientName: clientName || '',
                clientLocation: clientLocation || '',
                clientCaseId: clientCaseId || '',
                taxClientNumber: trimmedTaxClientNumber,
                AcceptedWithholdingTax: roundVatToInteger(AcceptedWithholdingTax),
                AcceptedWithholdingTaxMaximum: roundVatToInteger(AcceptedWithholdingTaxMaximum),
                incomeTaxAdvancePercentage: incomeTaxAdvancePercentage ?? 0,
                IncomeTaxAdvances: roundVatToInteger(IncomeTaxAdvances),
                total: roundVatToInteger(total),
                witholdingIncomesNoVat: roundVatToInteger(witholdingIncomesNoVat),
                StartDate: start,
                EndDate: end,
                useIncomeTaxAdvancePercentage: !!useIncomeTaxAdvancePercentage,
                incomeTaxAdvanceAmount: roundVatToInteger(
                    incomeTaxAdvanceAmount != null ? incomeTaxAdvanceAmount : 0
                ),
                surplusExpenses: surplusExpenses ?? 0
            };

            setWithholdingTax(builtTax);
            setOriginalWithholdingTax(builtTax);
        }

        // Always apply partner-specific calculations when a partner is selected
        // Uses originalWithholdingTax as the base (set above when item changes, or from previous render)
        const baseTax = hasItemChanged ? null : originalWithholdingTax;
        if (internalSelectedPartner && partners.length > 0) {
            const partner = partners.find(p => p.id === internalSelectedPartner);
            // Use the freshly-built tax if item just changed, otherwise use stored original
            const base = baseTax && Object.keys(baseTax).length > 0 ? baseTax : withholdingTax;
            if (partner && base && Object.keys(base).length > 0) {
                const equityMultiplier = partner.equity / 100;
                const partnerIncomesNoVat = Math.floor((base.witholdingIncomesNoVat || 0) * equityMultiplier);
                const partnerAcceptedWithholdingTax = Math.floor((base.AcceptedWithholdingTax || 0) * equityMultiplier);

                // Prefer resolved yearly settings from API; fall back to legacy partner fields, then base.
                const yearly = partnerAdvanceSettings?.[partner.id];
                const usePercentage =
                    yearly?.useIncomeTaxAdvancePercentage != null
                        ? !!yearly.useIncomeTaxAdvancePercentage
                        : (partner.useIncomeTaxAdvancePercentage ?? base.useIncomeTaxAdvancePercentage ?? true);
                const percentage =
                    yearly?.incomeTaxAdvancePercentage != null
                        ? Number(yearly.incomeTaxAdvancePercentage)
                        : (partner.withholdingTaxPercentage ?? base.incomeTaxAdvancePercentage ?? 0);
                const fixedAmount =
                    yearly?.incomeTaxAdvanceAmount != null
                        ? Number(yearly.incomeTaxAdvanceAmount)
                        : (partner.incomeTaxAdvanceAmount ?? base.incomeTaxAdvanceAmount ?? 0);

                let partnerTotal = usePercentage
                    ? Math.floor((partnerIncomesNoVat * percentage) / 100)
                    : fixedAmount;

                const partnerIncomeTaxAdvances = Math.max(0, partnerTotal - partnerAcceptedWithholdingTax);
                const partnerAcceptedWithholdingTaxMaximum = Math.min(partnerAcceptedWithholdingTax, partnerTotal);

                setWithholdingTax({
                    ...base,
                    witholdingIncomesNoVat: partnerIncomesNoVat,
                    total: partnerTotal,
                    AcceptedWithholdingTax: partnerAcceptedWithholdingTax,
                    AcceptedWithholdingTaxMaximum: partnerAcceptedWithholdingTaxMaximum,
                    IncomeTaxAdvances: partnerIncomeTaxAdvances,
                    surplusExpenses: Math.floor((base.surplusExpenses || 0) * equityMultiplier),
                    useIncomeTaxAdvancePercentage: usePercentage,
                    incomeTaxAdvancePercentage: usePercentage ? percentage : 0,
                    incomeTaxAdvanceAmount: usePercentage ? 0 : fixedAmount
                });
            }
        }
    }, [item, open, internalSelectedPartner, partners, partnerAdvanceSettings]);

    // ----------------------------------------
    // 2) Listen to custom events from the IFRAME
    //    (withholdingTaxUpdate)
    // ----------------------------------------

    const updateAcceptedWithholdingTaxMaximum = (val) => {
        const Val = parseFloat(val) || 0;

        setWithholdingTax(recalcAll({ AcceptedWithholdingTaxMaximum: Val }));
    };

    const updateSurplusExpenses = (val) => {
        const Val = parseFloat(val) || 0;
        setWithholdingTax(recalcAll({ surplusExpenses: Val }));
    };

    useEffect(() => {
        const handleWithholdingTaxMessage = (event) => {
            try {
                const message = JSON.parse(event.detail);
                if (message.source === 'withholdingTaxReport') {
                    switch (message.eventName) {
                        case 'updateAcceptedWithholdingTax':
                            updateAcceptedWithholdingTax(message.value);
                            break;
                        case 'updateIncomesNoVats':
                            updateIncomesNoVats(message.value);
                            break;
                        case 'updateIncomeTaxAdvancePercentage':
                            updateIncomeTaxAdvancePercentage(message.value);
                            break;
                        case 'updateIncomeTaxAdvanceAmount':
                            updateIncomeTaxAdvanceAmount(message.value);
                            break;

                        // NEW cases:
                        case 'updateAcceptedWithholdingTaxMaximum':
                            updateAcceptedWithholdingTaxMaximum(message.value);
                            break;
                        case 'updateSurplusExpenses':
                            updateSurplusExpenses(message.value);
                            break;

                        default:
                            break;
                    }
                }
            } catch (err) {
                console.error('Failed to parse withholdingTaxUpdate event:', event.detail);
            }
        };

        window.addEventListener('withholdingTaxUpdate', handleWithholdingTaxMessage);
        return () => {
            window.removeEventListener('withholdingTaxUpdate', handleWithholdingTaxMessage);
        };
    }, []);


    const handleDownloadComparativeAdvance = async () => {
        try {
            const date = withholdingTax.EndDate ?? new Date(); // Use EndDate as the reference year
            setLoading(true);

            if (!withholdingTax.clientCaseId || !date || !token) {
                toast.error(t("FailedToDownloadComparativeAdvancesReport"));
                return;
            }


            toast.success(t("ComparativeAdvancesReportIsDownloading"));
            await ReportsService.getYearlyAdvanceReport(withholdingTax.clientCaseId, user.accountantId, date, token, selectedPartner?.id || null);
            setLoading(false);

        } catch (error) {
            console.error("Error downloading yearly VAT report:", error);
            toast.error(t("FailedToDownloadComparativeAdvancesReport"));
            setLoading(false);

        }
    };

    // ----------------------------------------------------
    // 3) Helper update functions, replicating the logic:
    //    total = (withholdingTaxAdvance % or fixed),
    //    PaymentWithholdingTax = total - AcceptedWithholdingTax,
    //    AcceptedWithholdingTax <= total,
    //    PaymentWithholdingTax >= 0
    // ----------------------------------------------------
    // Example function that merges partial updates & forces integer math
    const recalcAll = (partial) => {
        return (prev) => {
            // 1) Merge old state + partial updates
            const merged = { ...prev, ...partial };

            let witholdingIncomesNoVat = roundVatToInteger(merged.witholdingIncomesNoVat);
            let percentage = merged.incomeTaxAdvancePercentage || 0;
            let surplus = roundVatToInteger(merged.surplusExpenses);

            // We'll derive newTotal from either percentage or 'merged.total'
            // depending on whether the user is in "percentage mode"
            let newTotal = 0;
            if (merged.useIncomeTaxAdvancePercentage) {
                // Round down (floor) so it's always an integer
                newTotal = Math.floor((witholdingIncomesNoVat * percentage) / 100);
            } else {
                // Otherwise, we read the "total" field the user can edit directly
                newTotal = roundVatToInteger(merged.total);
            }

            // 3) Get the existing 'accepted' and 'acceptedMax' from merged
            let accepted = roundVatToInteger(merged.AcceptedWithholdingTax);
            let acceptedMax = roundVatToInteger(merged.AcceptedWithholdingTaxMaximum);

            // 4) If the user changed "AcceptedWithholdingTax",
            //    then we automatically make acceptedMax = accepted.
            if (Object.prototype.hasOwnProperty.call(partial, 'AcceptedWithholdingTax')) {
                acceptedMax = accepted;
            }
            // If the user changed "AcceptedWithholdingTaxMaximum",
            // we do NOT touch 'accepted'.

            // 5) Now clamp acceptedMax to newTotal if it exceeds it
            if (acceptedMax > newTotal) {
                acceptedMax = newTotal;
            }

            // 6) If accepted > newTotal, clamp both to newTotal
            //    (i.e., if user typed a bigger 'accepted' than the newTotal)
            if (accepted > newTotal) {
                // accepted = newTotal;
                acceptedMax = newTotal;
            }

            // 7) Finally, compute Payment
            //    payment = newTotal - acceptedMax + surplus
            let payment = newTotal - acceptedMax + surplus;
            if (payment < 0) {
                payment = 0;
            }

            // 8) Return the fully-updated state
            return {
                ...merged,
                witholdingIncomesNoVat,
                incomeTaxAdvancePercentage: percentage,
                total: newTotal,
                AcceptedWithholdingTax: accepted,
                AcceptedWithholdingTaxMaximum: acceptedMax,
                surplusExpenses: surplus,
                IncomeTaxAdvances: payment,
            };
        };
    };





    const updateAcceptedWithholdingTax = (val) => {
        setWithholdingTax(recalcAll({ AcceptedWithholdingTax: roundVatToInteger(val) }));
    };

    const updateIncomesNoVats = (val) => {
        setWithholdingTax(recalcAll({ witholdingIncomesNoVat: roundVatToInteger(val) }));
    };

    const updateIncomeTaxAdvancePercentage = (val) => {
        setWithholdingTax(
            recalcAll({ incomeTaxAdvancePercentage: parseFloat(val) || 0, useIncomeTaxAdvancePercentage: true }),
        );
    };

    const updateIncomeTaxAdvanceAmount = (val) => {
        setWithholdingTax(
            recalcAll({ total: parseFloat(val) || 0, useIncomeTaxAdvancePercentage: false }),
        );
    };

    const showSurplusExpenses = item?.caseType
        ? Constants.SurplusExpensesCases.includes(item.caseType)
        : false;

    // Partner selection handler
    const handlePartnerChange = (partnerId) => {
        setInternalSelectedPartner(partnerId);
        
        // Find the selected partner
        const partner = partners.find(p => p.id === partnerId);
        if (partner) {
                // Calculate amounts based on partner's equity percentage
                const equityMultiplier = partner.equity / 100;
                
                // Calculate partner's portion of incomes
                const partnerIncomesNoVat = Math.floor(originalWithholdingTax.witholdingIncomesNoVat * equityMultiplier);
                const partnerAcceptedWithholdingTax = Math.floor(originalWithholdingTax.AcceptedWithholdingTax * equityMultiplier);
                
                // Use partner's specific tax settings to calculate total, with fallback to client settings
                // Fallback logic: if partner values are null/0, use client values from originalWithholdingTax
                const usePercentage = partner.useIncomeTaxAdvancePercentage ?? originalWithholdingTax.useIncomeTaxAdvancePercentage ?? true;
                const percentage = partner.withholdingTaxPercentage ?? originalWithholdingTax.incomeTaxAdvancePercentage ?? 0;
                const fixedAmount = partner.incomeTaxAdvanceAmount ?? originalWithholdingTax.incomeTaxAdvanceAmount ?? 0;
                
                let partnerTotal = 0;
                if (usePercentage) {
                    // Use percentage (partner's or client's fallback)
                    partnerTotal = Math.floor((partnerIncomesNoVat * percentage) / 100);
                } else {
                    // Use fixed amount (partner's or client's fallback)
                    partnerTotal = fixedAmount;
                }
                
                // Calculate partner's income tax advances
                const partnerIncomeTaxAdvances = Math.max(0, partnerTotal - partnerAcceptedWithholdingTax);
                
                // Calculate partner's accepted withholding tax maximum
                const partnerAcceptedWithholdingTaxMaximum = Math.min(partnerAcceptedWithholdingTax, partnerTotal);
                
                const partnerFilteredTax = {
                    ...originalWithholdingTax,
                    witholdingIncomesNoVat: partnerIncomesNoVat,
                    total: partnerTotal,
                    AcceptedWithholdingTax: partnerAcceptedWithholdingTax,
                    AcceptedWithholdingTaxMaximum: partnerAcceptedWithholdingTaxMaximum,
                    IncomeTaxAdvances: partnerIncomeTaxAdvances,
                    surplusExpenses: Math.floor(originalWithholdingTax.surplusExpenses * equityMultiplier),
                    // Update tax settings to match partner's settings (with fallback)
                    useIncomeTaxAdvancePercentage: usePercentage,
                    incomeTaxAdvancePercentage: usePercentage ? percentage : 0,
                    incomeTaxAdvanceAmount: usePercentage ? 0 : fixedAmount
                };
                
                setWithholdingTax(partnerFilteredTax);
            }
    };

    // Reset partner selection when modal closes
    const handleModalClose = () => {
        // Don't reset partner selection - keep it for next time modal opens
        onClose();
    };

    // ----------------------------------
    // 4) Generate HTML for the PDF
    // ----------------------------------
    const isEmptyObject = (obj) => {
        return Object.keys(obj).length === 0 && obj.constructor === Object;
    };

    function formatMonthYearUTC(dateString) {
        const date = new Date(dateString);

        // Extract the year and month in UTC
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth(); // zero-based (0 = January)

        // Construct a Date that is pinned to the 1st of the UTC month
        const utcDate = new Date(Date.UTC(year, month, 1));

        // Format that date in UTC, ensuring no local offset creeps in
        return utcDate.toLocaleString('he-IL', {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC'
        });
    }


    const generateWithholdingTaxHtml = () => {
        if (isEmptyObject(withholdingTax)) {
            return '';
        }
        // Example usage:
        const israelTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log("partners", partners);
        
        const currentSelectedPartner = internalSelectedPartner
            ? partners.find(p => p.id === internalSelectedPartner)
            : selectedPartner;
        const partnerNameText = currentSelectedPartner ? ` - ${t('Partner')}: ${currentSelectedPartner.name} (${currentSelectedPartner.equity}%)` : '';
        const partnerIdText = currentSelectedPartner ? currentSelectedPartner.partnerId: '';
        const displayBusinessNumber = partnerIdText
            ? `  ${partnerIdText}`
            : ((withholdingTax.taxClientNumber && String(withholdingTax.taxClientNumber).trim()) || withholdingTax.clientCaseId || '');
console.log(partnerNameText);

        const formattedDate = new Date().toLocaleString('he-IL', {
            timeZone: israelTimeZone,
            dateStyle: 'short',
            timeStyle: 'medium',
        });

        // Format both dates as month+year in UTC
        const startDateFormatted = formatMonthYearUTC(withholdingTax.StartDate);
        const endDateFormatted = formatMonthYearUTC(withholdingTax.EndDate);


        const dateRange = `${startDateFormatted} - ${endDateFormatted}`;



        // The snippet below is the HTML (including the embedded input fields)
        // that is displayed in an iframe-like structure
        return `
      <!DOCTYPE html>
      <html lang='en'>
      <head>
          <meta charset='UTF-8' />
          <meta http-equiv='X-UA-Compatible' content='IE=edge' />
          <meta name='viewport' content='width=device-width, initial-scale=1.0' />
          <style>
              a, button, input, select, h1, h2, h3, h4, h5, * {
                  box-sizing: border-box;
                  margin: 0;
                  padding: 0;
                  border: none;
                  text-decoration: none;
                  background: none;
                  -webkit-font-smoothing: antialiased;
              }
              menu, ol, ul {
                  list-style-type: none;
                  margin: 0;
                  padding: 0;
              }
              .body {
                  width: 100%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
              }
              .container {
                  width: 1288px;
                  transform-origin: top center;
                  transform: scale(0.68);
                  display: flex;
                  flex-direction: column;
                  align-items: center;
              }
              .header-WithholdingTax {
                  width: 100%;
                  text-align: center;
                  padding: 6px 0;
                  background: linear-gradient(135deg, #f5f7ff 0%, #eef0ff 100%);
                  border-radius: 8px 8px 0 0;
              }
              .header_container {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 20px;
              }
              .date, .title, .placeholder {
                  flex: 1;
                  text-align: center;
              }
              .title {
                  font-size: 22px;
                  font-weight: 700;
                  color: #304FFF;
              }
              .vat_percentage, .date {
                  font-size: 16px;
                  padding: 3px 0;
                  color: #333;
                  font-weight: 600;
              }
            .start-month-end-month {
                    color: #000000;
                    text-align: right;
                    font-family: 'Inter-Regular', sans-serif;
                    font-size: 22px;
                    font-weight: 400;
                    position: absolute;
                    left: 618px;
                    top: 142px;
                    width: 276px;
                    height: 34px;
                }
              .frame-3 {
                  box-sizing: border-box;
                  position: relative;
                  background: white;
                  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
              }
              .tax-advances-report-1-fotor-20240716175158-1 {
                  left: 0px;
                  top: 0px;
                  object-fit: cover;
                  width: 100%;
              }
              .client-name {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 583px;
                  top: 78px;
                  width: 449px;
                  height: 34px;
              }
              .incomeswithoutvat {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 382px;
                  top: 295px;
                  width: 354px;
                  height: 34px;
              }
              .income-tax-advances {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 382px;
                  top: 390px;
                  width: 354px;
                  height: 34px;
              }
              .total {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 912px;
                  top: 295px;
                  width: 324px;
                  height: 34px;
              }
              .percent {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 18px;
                  font-weight: bold;
                  position: absolute;
                  left: 771px;
                  top: 305px;
                  width: 66px;
                  height: 24px;
              }
              .income-tax-advances2 {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 912px;
                  top: 390px;
                  width: 324px;
                  height: 34px;
              }
              .total-income-tax-advances {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 826px;
                  top: 535px;
                  width: 404px;
                  height: 34px;
              }
              .client-case {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 85px;
                  top: 205px;
                  width: 204px;
                  height: 34px;
              }
              .year {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 482px;
                  top: 142px;
                  width: 97px;
                  height: 34px;
              }
              .client-location {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 12px;
                  top: 78px;
                  width: 522px;
                  height: 34px;
              }

                .surplus-expenses {
                  color: #000;
                  text-align: right;
                  font-family: 'Inter-Regular', sans-serif;
                  font-size: 22px;
                  font-weight: 400;
                  position: absolute;
                  left: 380px;
                  top: 480px;
                  width: 360px;
                  height: 34px;
                  background-color:white
              }
          </style>
          <title>Document</title>
      </head>
      <body>
          <div class='container'>
              <div class='header-WithholdingTax'>
                  <div class='header_container'>
                      <div class='title'>${withholdingTax.clientLocation}</div>
                      <div class='date'>עוסק מורשה: ${partnerIdText ? withholdingTax.clientCaseId : displayBusinessNumber}</div>
                      <div class='date'>${withholdingTax.clientName}${partnerNameText}</div>
                  </div>
                  <div class='header_container'>
                      <div class='date'>${formattedDate}</div>
                      <div class='title'>דוח מקדמות ע״פ מחזור</div>
                      <div class='placeholder'></div>
                  </div>
                  
                  <div class='vat_percentage'>
                  ${withholdingTax.useIncomeTaxAdvancePercentage ? `${withholdingTax.incomeTaxAdvancePercentage} % = אחוז מקדמות מהמחזור` : ""}
                  </div>
              </div>
              <div class='frame-3'>
                  <img class='tax-advances-report-1-fotor-20240716175158-1' src='${TaxAdvancesReportImage}' />
                  <div class='client-name'>${currentSelectedPartner ? `${currentSelectedPartner.name} (${currentSelectedPartner.equity}%)` : withholdingTax.clientName}</div>
                  <input
                      class='incomeswithoutvat input-field_Container_required-asterisk'
                      value="${withholdingTax.witholdingIncomesNoVat}"
                      onchange="window.parent.dispatchEvent(new CustomEvent('withholdingTaxUpdate', {
                        detail: JSON.stringify({
                          source: 'withholdingTaxReport',
                          eventName: 'updateIncomesNoVats',
                          value: this.value
                        })
                      }))"
                  />
                  <input
                      class='income-tax-advances input-field_Container_required-asterisk'
                      value="${withholdingTax.AcceptedWithholdingTax}"
                      onchange="window.parent.dispatchEvent(new CustomEvent('withholdingTaxUpdate', {
                        detail: JSON.stringify({
                          source: 'withholdingTaxReport',
                          eventName: 'updateAcceptedWithholdingTax',
                          value: this.value
                        })
                      }))"
                  />
                  ${withholdingTax.useIncomeTaxAdvancePercentage
                ? `
                        <input
                          class='percent input-field_Container_required-asterisk'
                          value="${withholdingTax.incomeTaxAdvancePercentage}"
                          onchange="window.parent.dispatchEvent(new CustomEvent('withholdingTaxUpdate', {
                            detail: JSON.stringify({
                              source: 'withholdingTaxReport',
                              eventName: 'updateIncomeTaxAdvancePercentage',
                              value: this.value
                            })
                          }))"
                        />
                      `
                : `<div class='percent'></div>`
            }
                  ${withholdingTax.useIncomeTaxAdvancePercentage
                ? `<div class='total'>${withholdingTax.total}</div>`
                : `
                        <input
                          class='total input-field_Container_required-asterisk'
                          value="${withholdingTax.total}"
                          onchange="window.parent.dispatchEvent(new CustomEvent('withholdingTaxUpdate', {
                            detail: JSON.stringify({
                              source: 'withholdingTaxReport',
                              eventName: 'updateIncomeTaxAdvanceAmount',
                              value: this.value
                            })
                          }))"
                        />
                      `
            }

                    <input
                      class='income-tax-advances2 input-field_Container_required-asterisk'
                      value='${withholdingTax.AcceptedWithholdingTaxMaximum}'
                      onchange="window.parent.dispatchEvent(new CustomEvent('withholdingTaxUpdate', {
                        detail: JSON.stringify({
                          source: 'withholdingTaxReport',
                          eventName: 'updateAcceptedWithholdingTaxMaximum',
                          value: this.value
                        })
                      }))"
                    />
              
                  <div class='total-income-tax-advances'>
                      ${withholdingTax.IncomeTaxAdvances}
                  </div>
                  <div class='client-case'>${displayBusinessNumber}</div>
                  <div class='year'>${endDate?.getUTCFullYear()}</div>
                  <div class='client-location'>${withholdingTax.clientLocation}</div>
                  <div class='start-month-end-month'>${dateRange}</div>
                  ${showSurplusExpenses
                ? `
                        <input
                          class='surplus-expenses input-field_Container_required-asterisk'
                          value='${withholdingTax.surplusExpenses}'
                          onchange="window.parent.dispatchEvent(new CustomEvent('withholdingTaxUpdate', {
                            detail: JSON.stringify({
                              source: 'withholdingTaxReport',
                              eventName: 'updateSurplusExpenses',
                              value: this.value
                            })
                          }))"
                        />
                      `
                : ''
            }
                  
              </div>
          </div>
      </body>
      </html>
    `;
    };

    // Create or update the HTML content each time `withholdingTax` changes
    useEffect(() => {
        setWithholdingHtmlContent(generateWithholdingTaxHtml());
    }, [withholdingTax]);

    // ----------------------------------
    // 5) Download as PDF
    // ----------------------------------
    const handleDownloadPdf = (ref) => {
        if (!ref?.current) return;
        
        const currentSelectedPartner = internalSelectedPartner
            ? partners.find(p => p.id === internalSelectedPartner)
            : selectedPartner;
        
        // Generate filename with partner name if available
        const partnerName = currentSelectedPartner ? `-${currentSelectedPartner.name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
        const filename = `withholdingTaxReport${partnerName}.pdf`;
        
        // Clone the element to avoid modifying the original
        const clonedElement = ref.current.cloneNode(true);
        
        // Remove red border classes from inputs before generating PDF
        const inputs = clonedElement.querySelectorAll('input.input-field_Container_required-asterisk');
        inputs.forEach(input => {
            input.classList.remove('input-field_Container_required-asterisk');
        });
        
        // Temporarily append to document to render
        clonedElement.style.position = 'absolute';
        clonedElement.style.left = '-9999px';
        document.body.appendChild(clonedElement);
        
        html2canvas(clonedElement).then((canvas) => {
            // Remove the cloned element
            document.body.removeChild(clonedElement);
            
            const data = canvas.toDataURL('image/png');
            const doc = new jsPDF({ orientation: 'l', unit: 'px', format: 'a4' });
            const imgProps = doc.getImageProperties(data);
            const pdfWidth = doc.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(data, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            doc.save(filename);
        }).catch((error) => {
            // Remove cloned element even on error
            if (document.body.contains(clonedElement)) {
                document.body.removeChild(clonedElement);
            }
            console.error('Error generating PDF:', error);
            toast.error(t('ErrorGeneratingPDF'));
        });
    };

    // ----------------------------------
    // 6) Save Manually => your logic
    // ----------------------------------
    const saveReportTaxManually = async (reportTax) => {
        try {
            // Get the currently selected partner (either from props or internal selection)
            const currentSelectedPartner = internalSelectedPartner ? partners.find(p => p.id === internalSelectedPartner) : selectedPartner;
            
            // If user sets "unreported", we store e.g. 2; if not => 1
            const statusValue = reportingWay === 'unreported' ? 2 : 1;
            reportTax.WithholdingTaxStatus = statusValue;

            // PaymentWithholdingTax can be the same as IncomeTaxAdvances
            // from local state
            const paymentWithholdingTax = withholdingTax.IncomeTaxAdvances;

            const payload = {
                caseId: reportTax.caseId,
                partnerId: currentSelectedPartner?.id || null, // Include PartnerId for partner-specific reporting
                AcceptedWithholdingTax: withholdingTax.AcceptedWithholdingTaxMaximum,
                PaymentWithholdingTax: paymentWithholdingTax,
                IncomeTaxAdvancePercentage: withholdingTax.useIncomeTaxAdvancePercentage
                    ? withholdingTax.incomeTaxAdvancePercentage
                    : 0,
                IncomeTaxAdvanceAmount: !withholdingTax.useIncomeTaxAdvancePercentage
                    ? withholdingTax.total
                    : 0,
                WitholdingIncomesNoVat: withholdingTax.witholdingIncomesNoVat,
                StartDate: withholdingTax.StartDate,
                EndDate: withholdingTax.EndDate,
                WithholdingTaxStatus: reportTax.WithholdingTaxStatus,
                surplusExpenses: parseFloat(withholdingTax.surplusExpenses),
            };

            let response;
            response = await ReportsService.createReportedAdvance(payload, token);

            if (response && response.ok) {
                const responseData = await response.json();
                toast.success(t('ReportedTaxUpdatedSuccess'),
                    {
                        autoClose: false, // Toast remains until user clicks to dismiss
                    });
                // Pass the current selected partner and date context to the data update callback
                const dateContext = {
                    isBiMonthly: isBiMonthly || isAdvancedBiMonthly,
                    isRange: isRange,
                    date: date || advancedDate,
                    startDate: startDate || advancedStartDate,
                    endDate: endDate || advancedEndDate,
                    biStartDate: biStartDate || advancedBiStartDate,
                    biEndDate: biEndDate || advancedBiEndDate
                };
                onDataUpdate(responseData, currentSelectedPartner?.id || null, dateContext);
            } else {
                toast.error(t('ReportedTaxUpdateFailed'));
            }
        } catch (error) {
            console.error(t('Error saving reported tax:'), error);
            toast.error(t('ReportedTaxSaveFailed'),
                {
                    autoClose: false, // Toast remains until user clicks to dismiss
                });
        }
    };

    // ----------------------------------
    // 7) Automatic => flow to the Tax Authority
    // ----------------------------------
    const startAuthCompletionCheck = async (authWindow, scope) => {
        // Similar logic to VatModal
        let attempts = 0;
        const maxAttempts = 25;
        const checkAuthCompletion = async () => {
            try {
                const result = await TaxAuthorityService.checkAuthCompletionAccountant(
                    user.accountantId,
                    scope,
                    token,
                );
                if (result.completed) {
                    setTaxAuthorityData((prev) => ({
                        ...prev,
                        resumeTaxAuthorityAdvancesOperation: true,
                    }));
                    authWindow.close();
                } else {
                    attempts += 1;
                    if (attempts < maxAttempts) {
                        setTimeout(checkAuthCompletion, 3000);
                    } else {
                        setLoading(false);
                        console.error('Token retrieval failed after maximum attempts.');
                        toast.error('Token retrieval failed after maximum attempts.');
                        authWindow.close();
                    }
                }
            } catch (err) {
                console.error('Error checking auth completion:', err);
                toast.error(t('Error checking auth completion'));
                authWindow.close();
                setLoading(false);
            }
        };
        checkAuthCompletion();
    };

    useEffect(() => {
        if (taxAuthorityData.resumeTaxAuthorityAdvancesOperation) {
            TaxAuthoritySendAdvancesReport(taxAuthorityData.UpdatedAdd, token);
        }
    }, [taxAuthorityData.resumeTaxAuthorityAdvancesOperation]);

    const TaxAuthoritySendAdvancesReport = async (data, tokenParam) => {
        setLoading(true);

        const currentSelectedPartner = internalSelectedPartner
            ? partners.find(p => p.id === internalSelectedPartner)
            : selectedPartner;

        /* 1) Build DTO exactly as the API expects ----------------------- */
        const sendAdvancesReportingDto = {
            CaseId: data.caseId,
            PartnerId: currentSelectedPartner?.id || null, // Include PartnerId for partner-specific reporting
            AccountantId: user.accountantId,
            Period: data.endDate,
            Income: data.witholdingIncomesNoVat,
            TaxWithHolding: data.acceptedWithholdingTax,
            PaymentAmount: data.paymentAmount,
            IncomeTaxAdvancePercentage: data.incomeTaxAdvancePercentage,
            IncomeTaxAdvanceAmount: data.incomeTaxAdvanceAmount,
            SurplusExpenses: data.surplusExpenses
        };

        try {
            /* 2) Call the service ---------------------------------------- */
            const response = await ReportsService.sendAdvancesReport(
                sendAdvancesReportingDto,
                tokenParam
            );

            /* 3) SUCCESS (HTTP 2xx) -------------------------------------- */
            if (response.ok) {
                await response.json(); // we only care that it succeeded
                toast.success(
                    t('AdvancesReportSuccess', {
                        caseId: data.caseId,
                        date: formatMonthYearDate(sendAdvancesReportingDto.Period)
                    }),
                    { autoClose: false }
                );
                // Get the currently selected partner for the automatic flow
                const currentSelectedPartner = internalSelectedPartner ? partners.find(p => p.id === internalSelectedPartner) : selectedPartner;
                await saveReportTaxManually(data);
                return;
            }

            /* 4) FAILURE (HTTP 4xx / 5xx) – parse once, throw once -------- */
            const err = await response.json();

            /* Normalise property names to lowercase for easier testing */
            const errMsg = err.Message ?? err.message ?? 'Unknown server error.';
            const errWrap = err.Error ?? err.error;
            const respWrap = err.Response ?? err.response;

            if (errWrap) {                                   // shape: { error: { … } }
                const num = errWrap.errorNumber ?? errWrap.ErrorNumber
                    ?? errWrap.code ?? errWrap.Code ?? 'N/A';
                const text = errWrap.supportMessage ?? errWrap.SupportMessage
                    ?? errWrap.errorDescription ?? errWrap.ErrorDescription
                    ?? 'No additional details.';

                throw { message: errMsg, errorNumber: num, supportMessage: text };
            }

            if (respWrap) {                                  // shape: { response: { … } }
                const list = respWrap.errorList ?? respWrap.ErrorList ?? [];
                const num = respWrap.updateCode ?? respWrap.UpdateCode ?? 'N/A';
                const text = list.length
                    ? list.map(e =>
                        `🔹 ${e.errorNumber ?? e.ErrorNumber ?? e.errorId ?? e.ErrorId}: `
                        + (e.supportMessage ?? e.SupportMessage ?? ''))
                        .join('\n')
                    : 'No additional details.';

                throw { message: errMsg, errorNumber: num, supportMessage: text };
            }

            /* Anything else */
            throw {
                message: 'Unexpected error format from server.',
                errorNumber: 'N/A',
                supportMessage: JSON.stringify(err)
            };
        }

        /* 5) UNIFIED catch block --------------------------------------- */
        catch (ex) {
            console.error('Advances report failure:', ex);

            toast.error(
                t('AdvancesReportError', {
                    caseId: data.caseId,
                    date: formatMonthYearDate(sendAdvancesReportingDto.Period),
                    errorNumber: ex.errorNumber ?? 'N/A',
                    supportMessage: ex.supportMessage ?? 'No additional details.'
                }),
                { autoClose: false }
            );
        }

        /* 6) Always clean up ------------------------------------------ */
        finally {
            handleModalClose();
            setLoading(false);
        }
    };


    // ----------------------------------
    // 8) Confirmation Modal flow
    // ----------------------------------
    const handleConfirmTaxAdvancesReport = async () => {

        const { UpdatedAdd } = taxAuthorityData;

        setIsConfirmationVisible(false);

        try {
            const tokenData = await TaxAuthorityService.requestTokenAccountant(
                user.accountantId,
                Constants.TaxAuthorityScopes.AdvancesPayment,
                token,
            );
            if (tokenData?.authUrl) {
                setTaxAuthorityData((prev) => ({
                    ...prev,
                    authUrl: tokenData.authUrl,
                    resumeTaxAuthorityAdvancesOperation: false,
                }));
                const authWindow = window.open(tokenData.authUrl, '_blank');
                handleModalClose();
                startAuthCompletionCheck(authWindow, Constants.TaxAuthorityScopes.AdvancesPayment);
            } else if (tokenData?.accessToken) {
                // If we got the token directly
                await TaxAuthoritySendAdvancesReport(UpdatedAdd, token);
            }
        } catch (err) {
            console.error('Error fetching token:', err);
            toast.error(t('ErrorFetchingToken'));
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (actionType === 'TaxAdvancesReport') {
            await handleConfirmTaxAdvancesReport();
        }
    };

    const getConfirmMessage = () => t('AreYouSureSendTaxAdvancesReport');
    const getTitleMessage = () => t('ConfirmTaxAdvancesReport');

    const deleteAdvanceReport = async (reportData, token) => {
        try {
            // Get the currently selected partner (either from props or internal selection)
            const currentSelectedPartner = internalSelectedPartner ? partners.find(p => p.id === internalSelectedPartner) : selectedPartner;
            
            const response = await ReportsService.deleteReportedAdvanceByPeriod({
                caseId: reportData.caseId,
                partnerId: currentSelectedPartner?.id || null, // Include PartnerId for partner-specific reporting
                StartDate: reportData.startDate,
                EndDate: reportData.endDate,
            }, token);

            if (response && response.ok) {
                toast.success(t('ReportedAdvanceDeletedSuccess'));
                const responseData = {
                    id: null,
                    caseId: withholdingTax.clientCaseId,
                    acceptedWithholdingTax: 0,
                    paymentWithholdingTax: 0,
                    witholdingIncomesNoVat: 0,
                    incomeTaxAdvancePercentage: 0,
                    incomeTaxAdvanceAmount: 0,
                    startDate: withholdingTax.StartDate,
                    endDate: withholdingTax.EndDate,
                    withholdingTaxStatus: 0,
                }
                // Pass the current selected partner and date context to the data update callback
                const dateContext = {
                    isBiMonthly: isBiMonthly || isAdvancedBiMonthly,
                    isRange: isRange,
                    date: date || advancedDate,
                    startDate: startDate || advancedStartDate,
                    endDate: endDate || advancedEndDate,
                    biStartDate: biStartDate || advancedBiStartDate,
                    biEndDate: biEndDate || advancedBiEndDate
                };
                onDataUpdate(responseData, currentSelectedPartner?.id || null, dateContext);
            } else {
                toast.error(t('ReportedAdvanceDeleteFailed'));
            }
            handleModalClose();
        } catch (error) {
            console.error(t('Error deleting Advance report:'), error);
            toast.error(t('ReportedAdvanceDeleteFailed'));
        }
    };
    // ----------------------------------
    // 9) Final "Save" button
    // ----------------------------------
    const handleSaveWithholdingTaxReport = async () => {
        const currentSelectedPartner = internalSelectedPartner
            ? partners.find(p => p.id === internalSelectedPartner)
            : selectedPartner;
        
        // Build a minimal object to pass around
        const UpdatedAdd = {
            id: withholdingTax.id,
            caseId: withholdingTax.clientCaseId,
            partnerId: currentSelectedPartner?.id || null, // Include PartnerId for partner-specific reporting
            startDate: withholdingTax.StartDate,
            endDate: withholdingTax.EndDate,
            acceptedWithholdingTax: parseFloat(withholdingTax.AcceptedWithholdingTaxMaximum),
            witholdingIncomesNoVat: parseFloat(withholdingTax.witholdingIncomesNoVat),
            status: 1,
            WithholdingTaxStatus: 1,
            paymentAmount: withholdingTax.IncomeTaxAdvances,
            incomeTaxAdvanceAmount: parseFloat(withholdingTax.incomeTaxAdvanceAmount),
            incomeTaxAdvancePercentage: parseFloat(withholdingTax.incomeTaxAdvancePercentage),
            surplusExpenses: parseFloat(withholdingTax.surplusExpenses),
        }

        setTaxAuthorityData((prev) => ({ ...prev, UpdatedAdd }));
        if (reportingWay === 'unreported') {
            await deleteAdvanceReport(UpdatedAdd, token);
        }
        else if (reportingWay === 'automatic') {
            // Show confirmation modal to do the Tax Authority flow
            setActionType('TaxAdvancesReport');
            setIsConfirmationVisible(true);
        } else {
            // Just do local save
            await saveReportTaxManually(UpdatedAdd);
            handleModalClose();
        }
    };

    // ----------------------------------
    // RENDER
    // ----------------------------------
    return (
        <Modal open={open} onClose={handleModalClose}>
            <>
                <Box sx={modalStyle}>
                    <div style={{ direction, display: 'flex', flexDirection: 'column', height: '100%' }}>

                        {/* Header: title + date pickers + partner selector + close */}
                        <WithholdingModalHeader
                            t={t}
                            direction={direction}
                            isRange={isRange}
                            isBiMonthly={isBiMonthly}
                            date={date}
                            startDate={startDate}
                            endDate={endDate}
                            biStartDate={biStartDate}
                            biEndDate={biEndDate}
                            showBiMonth={showBiMonth}
                            setShowBiMonth={setShowBiMonth}
                            handleDateChangeNew={handleDateChangeNew}
                            handleStartDateChangeNew={handleStartDateChangeNew}
                            handleEndDateChangeNew={handleEndDateChangeNew}
                            handleBiMonthDate={handleBiMonthDate}
                            handleDownloadComparativeAdvance={handleDownloadComparativeAdvance}
                            onClose={handleModalClose}
                            partners={partners}
                            internalSelectedPartner={internalSelectedPartner}
                            handlePartnerChange={handlePartnerChange}
                            isSubordinateClient={isSubordinateClient}
                        />

                        {/* Subordinate client warning */}
                        {isSubordinateClient && mainClientData && (
                            <div style={{
                                backgroundColor: '#ffebee', border: '1px solid #f44336',
                                borderRadius: '8px', padding: '8px 12px', margin: '4px 8px',
                                textAlign: 'center', fontSize: '12px', color: '#d32f2f', fontWeight: 'bold'
                            }}>
                                {t('SubordinateClientWarning')} - {mainClientData.clientName || mainClientData.name} ({mainClientData.caseId})
                            </div>
                        )}

                        {/* Main content: report image + accumulated panel */}
                        <div className="wht-modal-content">
                            <div className="wht-modal-content__report-wrapper" style={{ height: `${584 * 0.68 + 100}px` }}>
                                <div ref={withholdingContentRef} dangerouslySetInnerHTML={{ __html: withholdingHtmlContent }} />
                            </div>
                            <AccumulatedAdvancePanel t={t} accumulatedAdvance={accumulatedAdvance} />
                        </div>

                        {/* Action buttons + reporting options */}
                        <WithholdingModalActions
                            t={t}
                            direction={direction}
                            reportingWay={reportingWay}
                            setReportingWay={setReportingWay}
                            handleDownloadPdf={handleDownloadPdf}
                            withholdingContentRef={withholdingContentRef}
                            handleSaveWithholdingTaxReport={handleSaveWithholdingTaxReport}
                            onClose={handleModalClose}
                            isSubordinateClient={isSubordinateClient}
                        />
                    </div>
                </Box>

                {/* Confirmation Modal */}
                <CustomDeleteConfirmationModal
                    isVisible={isConfirmationVisible}
                    onClose={() => setIsConfirmationVisible(false)}
                    onDelete={handleConfirm}
                    confirmMessage={getConfirmMessage()}
                    titleMessage={getTitleMessage()}
                    type="sendReport"
                />

                {loading && (
                    <div className="loader-container">
                        <PuffLoader size={100} loading={loading} />
                    </div>
                )}
            </>
        </Modal>
    );
}
